import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingHeader from '../../components/ui/MarketingHeader';
import LoginForm from './components/LoginForm';
import TrustIndicators from './components/TrustIndicators';
import SocialAuthButtons from './components/SocialAuthButtons';
import Icon from '../../components/AppIcon';

const UserLogin = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      navigate('/app-dashboard');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />
      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                  <Icon name="LogIn" size={32} className="text-primary-foreground" />
                </div>
              </div>
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Access Your SAP Workspace
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Sign in to continue your SAP Best Practices implementation journey with enterprise-grade security and collaboration tools.
              </p>
            </div>

            {/* Login Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Login Form */}
              <div className="order-2 lg:order-1">
                <LoginForm />
              </div>

              {/* Social Auth & Info */}
              <div className="order-1 lg:order-2 space-y-8">
                <SocialAuthButtons />
                
                {/* Quick Access Info */}
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Quick Access Features
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mt-0.5">
                        <Icon name="Zap" size={16} className="text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground">
                          Resume Active Sessions
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          Continue where you left off with saved questionnaires and analysis
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center mt-0.5">
                        <Icon name="Users" size={16} className="text-success" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground">
                          Team Collaboration
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          Access shared projects and collaborate with your team members
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center mt-0.5">
                        <Icon name="FileText" size={16} className="text-warning" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground">
                          Document Library
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          Access your generated BPD² and FSD² documents instantly
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators Section */}
        <div className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Enterprise-Grade Security
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Your SAP implementation data is protected with industry-leading security measures and compliance certifications trusted by Fortune 500 companies.
              </p>
            </div>
            <TrustIndicators />
          </div>
        </div>

        {/* Support Section */}
        <div className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-card rounded-lg p-8 border border-border">
              <Icon name="HelpCircle" size={48} className="text-primary mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                Need Help Signing In?
              </h3>
              <p className="text-muted-foreground mb-6">
                Our support team is available 24/7 to help with account access, password resets, and enterprise SSO configuration.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 nav-transition">
                  <Icon name="MessageCircle" size={20} />
                  <span>Live Chat Support</span>
                </button>
                <button className="flex items-center justify-center space-x-2 px-6 py-3 border border-border rounded-lg hover:bg-muted nav-transition">
                  <Icon name="Mail" size={20} />
                  <span>Email Support</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Layers" size={20} className="text-primary-foreground" />
              </div>
              <span className="text-lg font-semibold text-foreground">
                BPMLflows
              </span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <button className="hover:text-foreground nav-transition">
                Privacy Policy
              </button>
              <button className="hover:text-foreground nav-transition">
                Terms of Service
              </button>
              <button className="hover:text-foreground nav-transition">
                Security
              </button>
              <span>© {new Date()?.getFullYear()} BPMLflows. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserLogin;